import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { baseURL } from '../../config'
import TelegramGroupsCard from './Notifications/TelegramGroupsCard'
import CompanyNotificationsCard from './Notifications/CompanyNotificationsCard'
import Toast from '../Loadboard/Searches/Toast'

export default function NotificationsSettings({ isDarkMode }) {
	const [loading, setLoading] = useState(true)
	const [groups, setGroups] = useState([])
	const [companySettings, setCompanySettings] = useState([])
	const [error, setError] = useState('')
	const [success, setSuccess] = useState('')
	const [unlinking, setUnlinking] = useState(null)
	const [requiresUpgrade, setRequiresUpgrade] = useState(false)

	const fetchData = useCallback(async () => {
		try {
			setLoading(true)
			const response = await axios.get(`${baseURL}/api/user/telegram-groups`)
			setGroups(response.data.groups || [])
			setCompanySettings(response.data.companySettings || [])
			setRequiresUpgrade(false)
		} catch (err) {
			if (err.response?.status === 402) {
				setRequiresUpgrade(true)
			} else {
				setError('Failed to load notification settings')
			}
		} finally {
			setLoading(false)
		}
	}, [])

	const [pendingAdd, setPendingAdd] = useState(false)

	useEffect(() => {
		fetchData()
	}, [fetchData])

	useEffect(() => {
		if (!pendingAdd) return
		const onFocus = () => {
			fetchData()
			setPendingAdd(false)
		}
		window.addEventListener('focus', onFocus)
		return () => window.removeEventListener('focus', onFocus)
	}, [pendingAdd, fetchData])

	useEffect(() => {
		if (!error) return
		const timer = setTimeout(() => setError(''), 10000)
		return () => clearTimeout(timer)
	}, [error])

	useEffect(() => {
		if (!success) return
		const timer = setTimeout(() => setSuccess(''), 5000)
		return () => clearTimeout(timer)
	}, [success])

	const handleUnlinkGroup = async groupId => {
		setUnlinking(groupId)
		try {
			await axios.delete(`${baseURL}/api/user/telegram-group/${groupId}`)
			await fetchData()
		} catch {
			setError('Failed to unlink group')
		} finally {
			setUnlinking(null)
		}
	}

	const handleUpdateSettings = async (companyName, settings, selectedTelegramGroup) => {
		try {
			const response = await axios.put(`${baseURL}/api/user/notification-settings`, {
				companyName,
				settings,
				selectedTelegramGroup,
			})
			setCompanySettings(prev =>
				prev.map(s =>
					s.companyName === companyName ? response.data.settings : s
				).concat(
					prev.some(s => s.companyName === companyName) ? [] : [response.data.settings]
				)
			)
			return { success: true }
		} catch (err) {
			const message = err.response?.data?.message || 'Failed to update settings'
			setError(message)
			return { success: false, message }
		}
	}

	const handleDeleteCompany = async companyName => {
		try {
			await axios.delete(`${baseURL}/api/user/notification-settings/${encodeURIComponent(companyName)}`)
			setCompanySettings(prev => prev.filter(s => s.companyName !== companyName))
		} catch (err) {
			setError(err.response?.data?.message || 'Failed to delete company notifications')
		}
	}

	const handleTestNotification = async (companyName, groupId) => {
		try {
			await axios.post(`${baseURL}/api/user/test-notification`, {
				companyName,
				groupId,
			})
			setSuccess('Test notification sent successfully')
		} catch (err) {
			setError(err.response?.data?.message || 'Failed to send test notification')
		}
	}

	if (loading) {
		return (
			<div className="flex justify-center items-center py-32">
				<div className={`animate-spin rounded-full h-12 w-12 border-4 border-t-transparent ${
					isDarkMode ? 'border-gray-400' : 'border-gray-500'
				}`} />
			</div>
		)
	}

	if (requiresUpgrade) {
		return (
			<div className="py-8 text-center">
				<svg
					className={`mx-auto h-12 w-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
					fill="currentColor"
					viewBox="0 0 24 24"
				>
					<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" />
				</svg>
				<h3 className={`mt-4 text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
					Telegram Notifications
				</h3>
				<p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
					Upgrade your subscription to receive real-time load alerts directly in your Telegram groups.
				</p>
				<a
					href="/settings/subscription"
					className="inline-block mt-6 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
				>
					Upgrade Subscription
				</a>
			</div>
		)
	}

	return (
		<div
			className="h-full overflow-y-auto space-y-6 max-w-4xl mx-auto p-6"
			style={{ colorScheme: isDarkMode ? 'dark' : 'light' }}
		>
			{error && <Toast message={error} error={true} />}
			{success && <Toast message={success} />}

			<TelegramGroupsCard
				isDarkMode={isDarkMode}
				groups={groups}
				unlinking={unlinking}
				onRemoveGroup={handleUnlinkGroup}
				onAddError={setError}
				onAddInitiated={() => setPendingAdd(true)}
			/>

			{companySettings.length > 0 && (
				<>
					<div className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} />

					<CompanyNotificationsCard
						isDarkMode={isDarkMode}
						companySettings={companySettings}
						groups={groups}
						onUpdateSettings={handleUpdateSettings}
						onDeleteCompany={handleDeleteCompany}
						onTestNotification={handleTestNotification}
					/>
				</>
			)}
		</div>
	)
}
